import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
  Button, HStack, Text, Box, Select, useToast, VStack, IconButton,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const MODAL_CONTEXT = {
  topup:   { title: 'Add more credits',       sub: null },
  upgrade: { title: 'Add more credits',       sub: null },
  project: { title: 'Add project slots',      sub: null },
  project_free: {
    title: "You've hit your project limit",
    sub: "Free plans include 3 projects. Upgrade to Pro to get 10 projects — plus extra slots if you need even more.",
  },
  credits: {
    title: "You're out of credits",
    sub: "Top up to keep your AI team working, or upgrade to Pro for a bigger monthly allowance.",
  },
  runLimit: {
    title: "You've hit the run limit for this project",
    sub: "Upgrade to Pro to unlock more runs, or top up credits to keep going on your current plan.",
  },
};

const CreditTopUpModal = ({ isOpen, onClose, hasSavedPaymentMethod = false, planType = 'free', initialMode = 'topup', resetAt = null, reason = null }) => {
  const [mode, setMode] = useState(initialMode);
  const [selectedCredits, setSelectedCredits] = useState(100);
  const [extraSlots, setExtraSlots] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [topupPacks, setTopupPacks] = useState([]);
  const [proMonthlyCredits, setProMonthlyCredits] = useState(300);
  const toast = useToast();

  const isFree = planType !== 'pro';

  // Sync mode when initialMode changes (e.g. opened from different buttons)
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode, isOpen]);

  useEffect(() => {
    fetchWithAuth('/api/subscription/topup-packs')
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setTopupPacks(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Comes from plan_configs (single source of truth, see
    // utils/planTiers.js) — 300 above is only the fallback before this
    // resolves, or if it fails.
    fetch('/api/subscription/pricing')
      .then((r) => r.json())
      .then((data) => {
        if (Number.isFinite(data?.proMonthlyCredits)) setProMonthlyCredits(data.proMonthlyCredits);
      })
      .catch(() => {});
  }, []);

  const pack = topupPacks.find(p => p.credits === selectedCredits) || topupPacks[2] || { credits: 100, priceNzd: 9, perCredit: 0.09 };
  const contextKey = reason === 'runLimit' ? 'runLimit'
    : reason === 'credits' ? 'credits'
    : isFree && initialMode === 'project' ? 'project_free'
    : mode;
  const { title: modalTitle, sub: modalSub } = MODAL_CONTEXT[contextKey] || MODAL_CONTEXT.topup;

  const handleClose = () => {
    setSuccess(null);
    setSelectedCredits(100);
    setExtraSlots(1);
    setMode(initialMode);
    onClose();
  };

  const handleBuy = async () => {
    if (mode === 'upgrade') {
      handleClose();
      window.location.href = '/upgrade';
      return;
    }

    if (mode === 'project') {
      setIsLoading(true);
      try {
        const res = await fetchWithAuth('/api/subscription/update-extra-projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: extraSlots }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setSuccess({ type: 'project', slots: extraSlots });
        } else {
          toast({ title: 'Error', description: data.error || 'Failed to add project slots.', status: 'error', duration: 4000, isClosable: true });
        }
      } catch {
        toast({ title: 'Error', description: 'Something went wrong. Please try again.', status: 'error', duration: 4000, isClosable: true });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      if (hasSavedPaymentMethod) {
        const res = await fetchWithAuth('/api/subscription/create-topup-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pack: selectedCredits }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setSuccess({ type: 'credits', amount: data.creditsAdded });
        } else {
          toast({ title: 'Payment failed', description: data.error || 'Please try again.', status: 'error', duration: 4000, isClosable: true });
        }
      } else {
        const res = await fetchWithAuth('/api/subscription/create-topup-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pack: selectedCredits }),
        });
        const data = await res.json();
        if (res.ok && data.url) {
          window.location.href = data.url;
        } else {
          toast({ title: 'Error', description: data.error || 'Failed to start checkout.', status: 'error', duration: 4000, isClosable: true });
        }
      }
    } catch {
      toast({ title: 'Error', description: 'Something went wrong. Please try again.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const buyLabel = mode === 'upgrade'
    ? 'Upgrade to Pro →'
    : mode === 'project'
      ? `Add ${extraSlots} slot${extraSlots !== 1 ? 's' : ''} — NZ$${extraSlots * 5}/mo`
      : hasSavedPaymentMethod
        ? `Buy ${pack.credits} credits — NZ$${pack.priceNzd}`
        : `Continue to checkout`;

  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="sm" isCentered>
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" p={2}>
          <ModalCloseButton />
          <ModalBody py={10}>
            <VStack spacing={4} align="center">
              <Text fontSize="3xl">✅</Text>
              {success.type === 'project' ? (
                <>
                  <Text fontSize="xl" fontWeight="bold" color="brand.600">+{success.slots} project slot{success.slots !== 1 ? 's' : ''} added!</Text>
                  <Text color="gray.500" textAlign="center" fontSize="sm">Your new slots are ready to use.</Text>
                </>
              ) : (
                <>
                  <Text fontSize="xl" fontWeight="bold" color="brand.600">+{success.amount} credits added!</Text>
                  <Text color="gray.500" textAlign="center" fontSize="sm">Your credits are ready to use.</Text>
                </>
              )}
              <Button colorScheme="brand" onClick={handleClose} w="full" borderRadius="xl" mt={2}>Done</Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" p={1}>
        <ModalCloseButton top={4} right={4} />
        <ModalBody pt={7} pb={6} px={6}>
          <Text fontSize="xl" fontWeight="700" color="gray.900" mb={1}>
            {modalTitle}
          </Text>
          <Text fontSize="sm" color="gray.500" mb={5}>
            {modalSub || (isFree
              ? 'Choose a one-time top-up or upgrade for more each month.'
              : 'Purchase credits or expand your project capacity.')}
          </Text>

          <VStack spacing={3} align="stretch" mb={5}>

            {/* Top-up credits option */}
            <Box
              border="2px"
              borderColor={mode === 'topup' ? 'gray.800' : 'gray.200'}
              borderRadius="xl"
              p={4}
              cursor="pointer"
              bg={mode === 'topup' ? 'white' : 'gray.50'}
              onClick={() => setMode('topup')}
              transition="all 0.12s"
            >
              <HStack justify="space-between" mb={mode === 'topup' ? 3 : 0}>
                <Text fontWeight="600" color="gray.800">Top up credits</Text>
                <Box
                  w={5} h={5} borderRadius="full"
                  border="2px" borderColor={mode === 'topup' ? 'gray.800' : 'gray.300'}
                  bg="white"
                  display="flex" alignItems="center" justifyContent="center"
                >
                  {mode === 'topup' && <Box w={2.5} h={2.5} borderRadius="full" bg="gray.800" />}
                </Box>
              </HStack>

              {mode === 'topup' && (
                <>
                  <Text fontSize="xs" color="gray.500" mb={3}>Purchase on demand. Use anytime.</Text>
                  <Select
                    size="sm"
                    borderRadius="lg"
                    bg="gray.100"
                    border="none"
                    fontWeight="medium"
                    value={selectedCredits}
                    onChange={e => setSelectedCredits(Number(e.target.value))}
                    _focus={{ boxShadow: 'none', bg: 'gray.200' }}
                    mb={2}
                  >
                    {topupPacks.map(p => (
                      <option key={p.credits} value={p.credits}>
                        +{p.credits.toLocaleString()} credits — NZ${p.priceNzd}
                        {p.bestValue ? ' (best value)' : ''}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="xs" color="gray.400">NZ${pack.perCredit.toFixed(2)}/credit</Text>
                </>
              )}
            </Box>

            {/* Extra project slots — Pro only */}
            {!isFree && (
              <Box
                border="2px"
                borderColor={mode === 'project' ? 'gray.800' : 'gray.200'}
                borderRadius="xl"
                p={4}
                cursor="pointer"
                bg={mode === 'project' ? 'white' : 'gray.50'}
                onClick={() => setMode('project')}
                transition="all 0.12s"
              >
                <HStack justify="space-between" mb={mode === 'project' ? 3 : 0}>
                  <Box>
                    <Text fontWeight="600" color="gray.800">Extra project slots</Text>
                    {mode !== 'project' && (
                      <Text fontSize="xs" color="gray.400" mt={0.5}>NZ$5/slot/month · added to your subscription</Text>
                    )}
                  </Box>
                  <Box
                    w={5} h={5} borderRadius="full"
                    border="2px" borderColor={mode === 'project' ? 'gray.800' : 'gray.300'}
                    bg="white"
                    display="flex" alignItems="center" justifyContent="center"
                    flexShrink={0}
                  >
                    {mode === 'project' && <Box w={2.5} h={2.5} borderRadius="full" bg="gray.800" />}
                  </Box>
                </HStack>

                {mode === 'project' && (
                  <>
                    <Text fontSize="xs" color="gray.500" mb={3}>Added to your monthly subscription. Cancel anytime.</Text>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.700">Number of slots</Text>
                      <HStack spacing={0}>
                        <IconButton
                          icon={<Text fontWeight="bold">−</Text>}
                          size="sm" variant="outline" borderRadius="lg"
                          onClick={(e) => { e.stopPropagation(); setExtraSlots(s => Math.max(1, s - 1)); }}
                          isDisabled={extraSlots === 1}
                          borderColor="gray.300" color="gray.600"
                          _hover={{ bg: 'gray.100' }}
                          w="32px" h="32px" minW="32px"
                        />
                        <Text fontSize="sm" fontWeight="600" color="gray.800" w="32px" textAlign="center">
                          {extraSlots}
                        </Text>
                        <IconButton
                          icon={<Text fontWeight="bold">+</Text>}
                          size="sm" variant="outline" borderRadius="lg"
                          onClick={(e) => { e.stopPropagation(); setExtraSlots(s => Math.min(20, s + 1)); }}
                          borderColor="gray.300" color="gray.600"
                          _hover={{ bg: 'gray.100' }}
                          w="32px" h="32px" minW="32px"
                        />
                      </HStack>
                    </HStack>
                    <Text fontSize="xs" color="gray.400" mt={2}>NZ${extraSlots * 5}/month added to your plan</Text>
                  </>
                )}
              </Box>
            )}

            {/* Upgrade option — free users only */}
            {isFree && (
              <Box
                border="2px"
                borderColor={mode === 'upgrade' ? 'gray.800' : 'gray.200'}
                borderRadius="xl"
                p={4}
                cursor="pointer"
                bg={mode === 'upgrade' ? 'white' : 'gray.50'}
                onClick={() => setMode('upgrade')}
                transition="all 0.12s"
              >
                <HStack justify="space-between" align="flex-start">
                  <Box flex={1}>
                    <HStack justify="space-between" align="center">
                      <Text fontWeight="600" color="gray.800">Upgrade to Pro</Text>
                      <Text fontSize="sm" fontWeight="700" color="gray.800" mr={2}>NZ$19.99/mo</Text>
                    </HStack>
                    {mode === 'upgrade' ? (
                      <VStack align="start" spacing={1} mt={2}>
                        {[
                          `${proMonthlyCredits} credits / month`,
                          '10 projects + extra project slots add-on',
                          'Priority AI processing',
                          'Enriched competitor data',
                          'Personalised branded shares',
                        ].map(f => (
                          <HStack key={f} spacing={2}>
                            <Text fontSize="xs" color="brand.600">✓</Text>
                            <Text fontSize="xs" color="gray.600">{f}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color="gray.400" mt={0.5}>
                        {proMonthlyCredits} credits/month · Extra project slots add-on · Priority AI
                      </Text>
                    )}
                  </Box>
                  <Box
                    w={5} h={5} borderRadius="full"
                    border="2px" borderColor={mode === 'upgrade' ? 'gray.800' : 'gray.300'}
                    bg="white"
                    display="flex" alignItems="center" justifyContent="center"
                    flexShrink={0}
                    mt={0.5}
                  >
                    {mode === 'upgrade' && <Box w={2.5} h={2.5} borderRadius="full" bg="gray.800" />}
                  </Box>
                </HStack>
              </Box>
            )}
          </VStack>

          {/* Footer */}
          <VStack spacing={2} align="stretch">
            <Button
              w="full" bg="gray.900" color="white" borderRadius="xl" size="md"
              _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.800' }}
              onClick={handleBuy}
              isLoading={isLoading}
              loadingText="Processing..."
              fontWeight="600"
            >
              {buyLabel}
            </Button>
            {mode !== 'project' && (
              <Button
                w="full" variant="outline" borderRadius="xl" size="md"
                onClick={handleClose}
                color="gray.600" borderColor="gray.200"
                _hover={{ bg: 'gray.50', color: 'gray.700' }}
                py={resetAt ? 6 : undefined}
              >
                <VStack spacing={0}>
                  <Text fontSize="sm" fontWeight="medium">
                    {resetAt ? 'Wait till it resets' : 'Cancel'}
                  </Text>
                  {resetAt && (
                    <Text fontSize="xs" color="gray.400" fontWeight="normal">
                      Credits reset {new Date(resetAt).toLocaleDateString('en-NZ', { month: 'long', day: 'numeric' })}
                    </Text>
                  )}
                </VStack>
              </Button>
            )}
            {mode === 'project' && (
              <Button
                w="full" variant="ghost" borderRadius="xl" size="md"
                onClick={handleClose} color="gray.500"
                _hover={{ bg: 'gray.50', color: 'gray.700' }}
              >
                Cancel
              </Button>
            )}
          </VStack>

          {(mode === 'topup' || mode === 'upgrade') && (
            <Text fontSize="xs" color="gray.400" textAlign="center" mt={3}>
              {hasSavedPaymentMethod && mode === 'topup'
                ? 'Uses your saved payment method.'
                : "You'll be redirected to Stripe to enter your payment details."}
            </Text>
          )}
          {mode === 'project' && (
            <Text fontSize="xs" color="gray.400" textAlign="center" mt={3}>
              Billed monthly · prorated from today · cancel anytime from billing settings.
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreditTopUpModal;
